import React, { useState, useRef, useEffect, useCallback } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { Typography } from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'
import styles from './styles'
import SectionEdit from './components/section-edit'
import AiLoading from './components/ai-loading'
import AiCourseReady from './components/ai-course-ready'
import {
  aICreatorGenerateCourseAction,
  aICreatorClearOutlineAction,
} from 'src/reducers/ai-creator/action'
import { getCourseCreationProgressAction } from 'src/reducers/course/action'
import { userLimitsUpdateCredits } from 'src/reducers/user-limits/action'
import { snackbarShowAction } from 'src/reducers/snackbar/action'
import SnackbarMessage from 'src/model/snackbar'
import { SnackbarType } from 'src/model/snackbar/type'
import { guid } from 'src/utils/guid'
import { track } from '@amplitude/analytics-browser'
import { useLocation } from 'react-router-dom'
import useOutlineLeaveGuard from './hooks/useOutlineLeaveGuard'
import useBlendedCourseProgress from './hooks/useBlendedCourseProgress'

const OUTLINE_WARNING_DESCRIPTION =
  'If you leave now, your AI outline will be lost and you must start over.'
const WAIT_SCREEN_WARNING_DESCRIPTION =
  'If you leave now, your AI course creation will be interrupted\nand you may need to start over.'
const GENERATION_FAILED_MESSAGE =
  'Something went wrong while creating your course. Please try again.'

const CreationStep = ({
  classes,
  onBackToOutline,
  imageWillBeGenerated,
  setImageWillBeGenerated,
  documentFollowing,
  setDocumentFollowing,
}) => {
  const outline = useSelector(state => state.aICreator.outline)
  const dispatch = useDispatch()
  const [courseGenerationLoading, setCourseGenerationLoading] = useState(false)
  const [courseData, setCourseData] = useState(null)
  const [courseFinished, setCourseFinished] = useState(false)
  const courseCompletedRef = useRef(false)
  const completionTransitionTimerRef = useRef(null)
  const progressIntervalRef = useRef(null)
  // Analytics context for the current generation run, captured when it starts.
  const generationRef = useRef({ startedAt: null, outlineId: null, courseId: null })
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const isLeaveGuardEnabled = !courseFinished
  const {
    displayedProgress: courseProgress,
    isCompletionSettled,
    consumeBackendProgress,
  } = useBlendedCourseProgress(courseGenerationLoading)

  useOutlineLeaveGuard(
    isLeaveGuardEnabled,
    courseGenerationLoading
      ? WAIT_SCREEN_WARNING_DESCRIPTION
      : OUTLINE_WARNING_DESCRIPTION,
  )

  const getGenerationEventProperties = useCallback(() => {
    const { startedAt, outlineId, courseId } = generationRef.current
    return {
      courseId,
      outlineId,
      durationMs: startedAt ? Date.now() - startedAt : null,
    }
  }, [])

  const clearCompletionTransitionTimer = useCallback(() => {
    if (completionTransitionTimerRef.current) {
      clearTimeout(completionTransitionTimerRef.current)
      completionTransitionTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!courseGenerationLoading || !isCompletionSettled || courseCompletedRef.current) {
      return
    }

    courseCompletedRef.current = true

    // Keep existing behavior: show 100% briefly before transitioning.
    completionTransitionTimerRef.current = setTimeout(() => {
      setCourseGenerationLoading(false)
      setCourseFinished(true)
      track('course_generated_successfully', getGenerationEventProperties())
      clearCompletionTransitionTimer()
    }, 600)

    return () => {
      clearCompletionTransitionTimer()
    }
  }, [
    courseGenerationLoading,
    isCompletionSettled,
    clearCompletionTransitionTimer,
    getGenerationEventProperties,
  ])

  useEffect(() => {
    return () => {
      clearCompletionTransitionTimer()
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }
    }
  }, [clearCompletionTransitionTimer])

  const collectionId = searchParams.get('collectionId')
  const consumeBackendProgressRef = useRef(consumeBackendProgress)
  consumeBackendProgressRef.current = consumeBackendProgress

  const handleGenerationFailed = useCallback(
    reason => {
      if (courseCompletedRef.current) {
        return
      }
      courseCompletedRef.current = true

      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }

      track('course_generation_failed', {
        ...getGenerationEventProperties(),
        reason: reason || null,
      })

      setCourseGenerationLoading(false)
      setCourseData(null)
      dispatch(
        snackbarShowAction(
          new SnackbarMessage(guid().uid(), SnackbarType.ERROR, [
            GENERATION_FAILED_MESSAGE,
          ]),
        ),
      )
      // The outline was cleared when generation started, so there is nothing to edit here.
      if (onBackToOutline) {
        onBackToOutline()
      }
    },
    [dispatch, getGenerationEventProperties, onBackToOutline],
  )

  const handleGenerationFailedRef = useRef(handleGenerationFailed)
  handleGenerationFailedRef.current = handleGenerationFailed

  const checkCourseProgress = useCallback(courseId => {
    dispatch(
      getCourseCreationProgressAction(courseId, response => {
        // The backend reports "failed" when the course or its pages no longer exist.
        if (response?.status === 'failed') {
          handleGenerationFailedRef.current(response.failureReason)
          return
        }

        const normalized = consumeBackendProgressRef.current(response)

        if (normalized.isComplete && progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current)
          progressIntervalRef.current = null
        }
      }),
    )
  }, [dispatch])

  const scrollToTop = () => {
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }

  const handleGenerateCourse = totalCredits => {
    track('started_generating_course')
    generationRef.current = {
      startedAt: Date.now(),
      outlineId: outline?.response?.outlineId ?? null,
      courseId: null,
    }
    scrollToTop()
    clearCompletionTransitionTimer()
    courseCompletedRef.current = false
    setCourseGenerationLoading(true)

    // Update global user limits
    dispatch(userLimitsUpdateCredits(totalCredits))
    dispatch(aICreatorClearOutlineAction())
    // Prepare course generation request
    const validCollectionId =
      collectionId === 'undefined' ||
      collectionId === 'null' ||
      collectionId === null ||
      collectionId === undefined ||
      collectionId === ''
        ? null
        : collectionId
    const requestData = {
      outlineId: outline?.response?.outlineId,
      collectionId: validCollectionId,
    }

    dispatch(
      aICreatorGenerateCourseAction(requestData, response => {
        if (response && response.courseId) {
          generationRef.current.courseId = response.courseId
          setCourseData({
            code: response.courseCode,
            id: response.courseId,
          })

          // Start progress checking
          progressIntervalRef.current = setInterval(() => {
            checkCourseProgress(response.courseId)
          }, 1000)
        } else {
          setCourseGenerationLoading(false)
        }
      }),
    )
  }

  return (
    <div
      className={classes.root}
      style={
        courseGenerationLoading
          ? {
              maxWidth: '1280px',
              width: '100%',
              marginTop: '4px',
              paddingTop: '0',
              gap: '4px',
            }
          : undefined
      }
    >
      {courseGenerationLoading ? (
        <AiLoading progress={courseProgress} />
      ) : courseFinished && courseData ? (
        <AiCourseReady courseData={courseData} />
      ) : (
        <>
          <div className={classes.header}>
            <Typography className={classes.title}>Set the context</Typography>
            <Typography className={classes.subtitle}>
              You can edit the context, make additions and removals, and revise
              the order as you wish.
            </Typography>
          </div>
          <SectionEdit
            imageWillBeGenerated={imageWillBeGenerated}
            setImageWillBeGenerated={setImageWillBeGenerated}
            onGenerateCourse={handleGenerateCourse}
            documentFollowing={documentFollowing}
            setDocumentFollowing={setDocumentFollowing}
          />
        </>
      )}
    </div>
  )
}

export default withStyles(styles)(CreationStep)
